import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useFounding } from '@/hooks/useConsoleData';
import {
  DUE_LABEL,
  FEEDBACK_KIND_LABEL,
  FOUNDING_STATUSES,
  SOURCES,
  SOURCE_LABEL,
  STATUS_LABEL,
  addFounding,
  addResultWords,
  dateInputValue,
  daysAgoWords,
  rowLine,
  streakWords,
  updateFounding,
  type FoundingRow,
  type FoundingStatus,
} from '@/lib/founding-data';
import { num, when } from '@/lib/format';

/** The message an API error carries, or a fallback; nothing here is invented. */
const errorWords = (e: unknown, fallback: string): string => {
  const x = e as { response?: { data?: { message?: string } }; message?: string } | null;
  return x?.response?.data?.message ?? x?.message ?? fallback;
};
import s from './console.module.css';

/**
 * Founding (FOUNDING_MEMBERS_2026-10-17.md): the ten to twenty people Brian
 * invited personally. One row each: where they are in the loop, their last
 * check and streak (live), what they said that nobody has answered, the plan
 * (the free founding period, paid, or free), the call, and the next action
 * Brian owes them with its due date. The order is what he owes first.
 *
 * Add by email: pv-auth grants the founding period as a comp, tags the GHL
 * contact founding-member and makes the card in the Founding members
 * pipeline; the page says what of that happened. Edits save on blur or on
 * the button; a status change moves the GHL card.
 */
export default function FoundingPage() {
  const qc = useQueryClient();
  const q = useFounding();
  const d = q.data;
  const [filter, setFilter] = useState<FoundingStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['founding'] });

  const shown = useMemo(() => {
    if (!d) return [];
    const needle = search.trim().toLowerCase();
    return d.rows.filter((r) => (filter === 'all' || r.status === filter) && (!needle || `${r.name} ${r.email} ${r.nextAction ?? ''}`.toLowerCase().includes(needle)));
  }, [d, filter, search]);

  return (
    <div className={s.stack}>
      <div className={s.pageHead}>
        <h1 className={s.pageTitle}>Founding</h1>
        <span className={s.pageSub}>{d ? `${num(d.summary.total)} founding ${d.summary.total === 1 ? 'member' : 'members'} · ${num(d.summary.owed)} owed something today · ${num(d.summary.withOpenFeedback)} with unanswered feedback · refreshed ${when(d.generatedAt)}` : ''}</span>
      </div>

      {q.isError && <div className={s.error}>Could not load the founding list. This page needs GET /provider/admin/founding.</div>}
      {d && !d.pipeline.configured && (
        <div className={s.jWarn} data-panel="founding-pipeline">GHL pipeline not wired: {d.pipeline.reason ?? 'unknown'} Members are still tagged {`founding-member`} and listed here; the card appears once the pipeline exists.</div>
      )}
      {notice && <div className={s.notice} role="status">{notice} <button type="button" className={s.linkBtn} onClick={() => setNotice(null)}>Close</button></div>}

      <AddForm onDone={(line) => { setNotice(line); refresh(); }} />

      {d && (
        <div className={`${s.strip} ${s.scrollRow}`} data-panel="founding-counts">
          {(['all', ...FOUNDING_STATUSES] as const).map((st) => (
            <button key={st} type="button" className={`${s.stat} ${filter === st ? s.personFocus : ''}`} style={{ cursor: 'pointer', textAlign: 'left' }} onClick={() => setFilter(st)} aria-pressed={filter === st}>
              <span className={s.statLabel}>{st === 'all' ? 'All' : STATUS_LABEL[st]}</span>
              <span className={s.statVal}>{st === 'all' ? num(d.summary.total) : num(d.summary.byStatus[st])}</span>
            </button>
          ))}
        </div>
      )}

      <div className={s.filters}>
        <input className={s.search} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search names, emails, next actions" aria-label="Search founding members" />
      </div>

      <div className={s.told} data-panel="founding-rows">
        {d && shown.length === 0 && <div className={s.empty}>{d.rows.length === 0 ? 'Nobody on the founding list yet. Add the first by email above.' : 'Nothing matches.'}</div>}
        {shown.map((r) => (
          <FoundingRowCard key={r.userId} row={r} onSaved={(line) => { if (line) setNotice(line); refresh(); }} />
        ))}
      </div>
    </div>
  );
}

function AddForm({ onDone }: { onDone: (line: string) => void }) {
  const [email, setEmail] = useState('');
  const [source, setSource] = useState<string>('two_checks');
  const [compDays, setCompDays] = useState('90');
  const [nextAction, setNextAction] = useState('Send the founding invite');
  const [due, setDue] = useState(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      const out = await addFounding({ email: email.trim(), source, compDays: compDays === '' ? null : Number(compDays), nextAction: nextAction.trim() || null, nextActionDueAt: due || null });
      onDone(addResultWords(out));
      setEmail('');
    } catch (e: unknown) {
      setErr(errorWords(e, 'Could not add.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={s.panel} data-panel="founding-add">
      <div className={s.panelHead}><span className={s.panelTitle}>Add a founding member</span></div>
      <div className={s.filters}>
        <input className={s.search} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Member email (must have an account)" aria-label="Email" type="email" />
        <select className={s.selectInline} value={source} onChange={(e) => setSource(e.target.value)} aria-label="Why them">
          {SOURCES.map((k) => <option key={k} value={k}>{SOURCE_LABEL[k]}</option>)}
        </select>
        <input className={s.selectInline} value={compDays} onChange={(e) => setCompDays(e.target.value)} aria-label="Free days" type="number" min={0} max={730} style={{ width: '6em' }} title="Days of free founding period (0 grants nothing)" />
        <input className={s.search} value={nextAction} onChange={(e) => setNextAction(e.target.value)} placeholder="Next action" aria-label="Next action" />
        <input className={s.selectInline} value={due} onChange={(e) => setDue(e.target.value)} aria-label="Due" type="date" />
        <button type="button" className={s.btn} disabled={busy || !email.trim()} onClick={() => void submit()}>{busy ? 'Adding' : 'Add'}</button>
      </div>
      {err && <div className={s.error}>{err}</div>}
    </div>
  );
}

function FoundingRowCard({ row, onSaved }: { row: FoundingRow; onSaved: (line: string | null) => void }) {
  const [nextAction, setNextAction] = useState(row.nextAction ?? '');
  const [due, setDue] = useState(dateInputValue(row.nextActionDueAt));
  const [callAt, setCallAt] = useState(dateInputValue(row.callAt));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const save = async (patch: Parameters<typeof updateFounding>[1], line: string | null = null) => {
    setBusy(true);
    setErr(null);
    try {
      const out = await updateFounding(row.userId, patch);
      onSaved(line ?? (out.ghl ? (out.ghl.opportunityId ? `${row.firstName || row.email}: ${STATUS_LABEL[out.status]}, GHL card moved.` : `${row.firstName || row.email}: ${STATUS_LABEL[out.status]}; GHL card not moved (${out.ghl.tagged ? 'pipeline missing' : 'no contact'}).`) : null));
    } catch (e: unknown) {
      setErr(errorWords(e, 'Could not save.'));
    } finally {
      setBusy(false);
    }
  };
  const dirty = nextAction !== (row.nextAction ?? '') || due !== dateInputValue(row.nextActionDueAt) || callAt !== dateInputValue(row.callAt);

  return (
    <div className={`${s.toldRow} ${row.openFeedbackCount > 0 ? s.toldRowFlag : ''}`} data-founding={row.userId} data-due={row.nextActionDue}>
      <div className={s.personTop}>
        <div>
          <Link className={s.rowLink} to={`/people/${encodeURIComponent(row.userId)}`}>{row.name || row.email}</Link>
          <span className={s.muted}> {row.email}{row.phone ? ' · phone on file' : ''}{row.verified ? '' : ' · not verified'}{row.source ? ` · ${SOURCE_LABEL[row.source] ?? row.source}` : ''}</span>
          <div className={s.sub}>{rowLine(row)}</div>
        </div>
        <select className={s.selectInline} value={row.status} aria-label={`Status of ${row.name || row.email}`} disabled={busy} onChange={(e) => void save({ status: e.target.value as FoundingStatus })}>
          {FOUNDING_STATUSES.map((st) => <option key={st} value={st}>{STATUS_LABEL[st]}</option>)}
        </select>
      </div>

      <div className={s.pCardNums}>
        <span className={s.pill} data-col="last-check">Last check {daysAgoWords(row.lastCheckAt)}</span>
        <span className={s.pill} data-col="streak">Streak {streakWords(row.streak)}</span>
        <span className={s.pill} data-col="checks">{num(row.checks)} checks on {num(row.checkDays)} days</span>
        <span className={s.pill} data-col="plan">{row.plan.label}{row.plan.endsAt ? ` to ${row.plan.endsAt.slice(0, 10)}` : ''}</span>
        <span className={`${s.pill} ${row.openFeedbackCount > 0 ? s.chipUnread : ''}`} data-col="feedback">{row.openFeedbackCount > 0 ? `${num(row.openFeedbackCount)} unanswered` : 'nothing unanswered'}</span>
        <span className={`${s.pill} ${row.nextActionDue === 'overdue' || row.nextActionDue === 'today' ? s.chipFlag : ''}`} data-col="due">{DUE_LABEL[row.nextActionDue]}</span>
        {row.ghl.contactUrl && <a className={s.pill} href={row.ghl.contactUrl} target="_blank" rel="noreferrer">GHL</a>}
      </div>

      {row.openFeedback.length > 0 && (
        <div className={s.toldText} data-col="open-feedback">
          {row.openFeedback.map((f, i) => <div key={i}><span className={s.muted}>{f.at.slice(0, 10)}, {FEEDBACK_KIND_LABEL[f.kind]}:</span> {f.text}</div>)}
        </div>
      )}

      <div className={s.filters}>
        <input className={s.search} value={nextAction} onChange={(e) => setNextAction(e.target.value)} placeholder="Next action Brian owes them" aria-label={`Next action for ${row.name || row.email}`} />
        <input className={s.selectInline} value={due} onChange={(e) => setDue(e.target.value)} aria-label="Due date" type="date" />
        <button type="button" className={s.linkBtn} onClick={() => setOpen((v) => !v)} aria-expanded={open}>{open ? 'Less' : 'Call and more'}</button>
        <button type="button" className={s.btn} disabled={busy || !dirty} onClick={() => void save({ nextAction: nextAction.trim() || null, nextActionDueAt: due || null, callAt: callAt || null }, `${row.firstName || row.email}: saved.`)}>{busy ? 'Saving' : 'Save'}</button>
      </div>
      {open && (
        <div className={s.filters} data-col="more">
          <label className={s.muted}>Call <input className={s.selectInline} value={callAt} onChange={(e) => setCallAt(e.target.value)} aria-label="Call date" type="date" /></label>
          <button type="button" className={s.btnGhost} disabled={busy || Boolean(row.callDoneAt)} onClick={() => void save({ callDoneAt: new Date().toISOString() }, `${row.firstName || row.email}: call marked done.`)}>{row.callDoneAt ? `Call done ${row.callDoneAt.slice(0, 10)}` : 'Mark call done'}</button>
          <button type="button" className={s.btnGhost} disabled={busy} onClick={() => void save({ weeklyNoteSent: true }, `${row.firstName || row.email}: weekly note stamped.`)}>Weekly note sent</button>
          <button type="button" className={s.btnGhost} disabled={busy} onClick={() => void save({ namePublicOk: !row.namePublicOkAt }, null)}>{row.namePublicOkAt ? 'Name public: yes (undo)' : 'Name public: no (set yes)'}</button>
        </div>
      )}
      {err && <div className={s.error}>{err}</div>}
    </div>
  );
}
